import { Group, Stack } from "@mantine/core"
import { RowSelectionState } from "@tanstack/react-table"
import { InfoBox, Loader, ColumnManager, FilterBuilder } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { InlineBanner } from "~/components/InlineBanner"
import { SortBuilder } from "~/components/SortBuilder"
import { CustomTable } from "~/components/table/CustomTable"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindProductsCustomFieldsQueryResponse,
  FindProductsCustomFieldsRequestFiltersDto,
  useFindProductsCustomFields,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/hooks/useGetOrganizationSettingsQuery"
import { useBuildCustomFieldsSorts } from "~/pages/Brand/Products/List/Components/ProductsTables/custom-fields/hooks/useBuildCustomFieldsSorts"
import { useProductsCustomFieldsTable } from "~/pages/Brand/Products/List/Components/ProductsTables/custom-fields/useCustomFieldsTable"
import { useCustomFieldsHeaders } from "~/pages/Brand/Products/List/Components/headers.tsx"
import { useBuildProductFilters } from "~/pages/Brand/Products/List/hooks/useBuildProductFilters"
import { useAuth } from "~/store/auth-context.tsx"

export const CustomFieldsProductsTable = () => {
  const { t } = useTranslation()
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { user, isAdmin } = useAuth()

  const { title, searchPlaceholder, options, filtersValue, setFiltersValue } =
    useBuildProductFilters<FindProductsCustomFieldsRequestFiltersDto>()

  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)

  const { sorts, setSorts, sortOptions } = useBuildCustomFieldsSorts()

  const {
    data: productsData,
    isPending: isLoadingProducts,
    isFetching: isFetchingProducts,
    setPageBasedPagination,
    pageBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindProductsCustomFields,
    FindProductsCustomFieldsQueryResponse
  >(useFindProductsCustomFields, {
    filters: filtersValue,
    sorts,
  })

  const products = useMemo(() => productsData?.data ?? [], [productsData?.data])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)
  const customHeaders = useCustomFieldsHeaders(products)

  const table = useProductsCustomFieldsTable({
    pagination: pageBasedPagination,
    rowSelection,
    setPagination: setPageBasedPagination,
    setRowSelection,
    pageCount: totalPages,
    products,
    customFieldKeys: Object.keys(customHeaders),
  })

  let adminMessage: string | null = null

  if (org?.settings?.customFieldsPlan !== "EXPERT") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("simulation.notSubscribed.title")}
          message={t("simulation.notSubscribed.message")}
          variant="info"
        />
      )
    }
  }

  return (
    <Stack>
      {adminMessage && (
        <InlineBanner
          type="admin"
          size="xs"
          message={t("simulation.notSubscribed.title")}
        >
          {adminMessage}
        </InlineBanner>
      )}
      <Views tableContext={TABLE_CONTEXT_MAP.PRODUCTS_CUSTOM_FIELDS} />
      <Group>
        <SearchBar
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          placeholder={t("products:lists.placeholders.searchProduct")}
          isFetching={isFetchingProducts}
        />
        <SortBuilder sorts={sorts} setSorts={setSorts} options={sortOptions} />
        <FilterBuilder
          title={title}
          searchPlaceholder={searchPlaceholder}
          options={options}
          setFilters={setFiltersValue}
          filters={filtersValue}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <ViewHeader tableContext={TABLE_CONTEXT_MAP.PRODUCTS_CUSTOM_FIELDS} />
      </Group>
      {isLoadingProducts ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          manualPaginationCount={productsData?.count || 0}
          stickyHeader
          layout="fixed"
          setOpenColumnManager={setIsColumnManagerOpen}
        />
      )}
    </Stack>
  )
}
